/**
 * ExpensesScreen — the new trip home.
 *
 * This is the redesigned trip landing page that merges Overview + Transactions
 * into one clean screen. The design goal: "minimize work for the user, maximize
 * automation." The user sees cleaned-up merchant names, knows immediately what
 * they're owed, and can get to settlement in one tap.
 *
 * Layout (top to bottom):
 *   1. Header — trip name + people row (same PeopleSheet trigger as TripOverviewPage)
 *   2. Money summary — total spent stat + MyBalanceWidget
 *   3. "Needs a quick look" nudge — amber banner when any transaction needs review
 *   4. Transaction list — grouped by day, newest first, category icons, review badges
 *   5. "+ Add" floating button (interim: navigates to add-interim route)
 *   6. Sticky "Settle up" button at the bottom
 */
import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import {
  Users, Plus, ChevronRight, AlertTriangle, Hourglass,
  Utensils, Car, Building, Coffee, ShoppingCart, Wine, Fuel,
  ShoppingBag, Receipt,
} from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../contexts/AuthContext'
import { displayName, needsReview } from '../lib/expenseDisplay'
import MyBalanceWidget, { CURRENCY_SYMBOLS } from '../components/MyBalanceWidget'
import StatCard from '../components/StatCard'
import PeopleSheet from '../components/PeopleSheet'
import AddSheet from '../components/AddSheet'
import RecurringExpenses from '../components/RecurringExpenses'
import GroupNotFound from '../components/GroupNotFound'

// ── Category icon map ─────────────────────────────────────────────────────────
// Maps a transaction category string to a Lucide icon component.
// All names verified against lucide-react v0.468.
const CATEGORY_ICONS = {
  dining:    Utensils,
  food:      Utensils,
  transport: Car,
  travel:    Car,
  lodging:   Building,
  hotel:     Building,
  coffee:    Coffee,
  groceries: ShoppingCart,
  drinks:    Wine,
  fuel:      Fuel,
  shopping:  ShoppingBag,
}

// Returns the right icon component for a category, defaulting to Receipt.
function categoryIcon(category) {
  return CATEGORY_ICONS[category?.toLowerCase()] || Receipt
}

// ── Member avatar colors — matches TripOverviewPage for consistency ───────────
const MEMBER_COLORS = [
  'bg-lime-400 text-ink-950',
  'bg-green-400 text-ink-950',
  'bg-amber-400 text-ink-950',
  'bg-red-400 text-white',
  'bg-blue-400 text-white',
]

// ── Date helpers ──────────────────────────────────────────────────────────────
function formatDateLabel(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ── Amount formatter ──────────────────────────────────────────────────────────
function formatAmount(amount, currency) {
  const sym = CURRENCY_SYMBOLS[currency] || (currency + ' ')
  return `${sym}${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: currency === 'JPY' ? 0 : 2,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  })}`
}

// ── Who-splits summary ────────────────────────────────────────────────────────
// Returns a short string like "split 4" or "You · Anthony" for the row meta.
export function splitSummary(txn, members) {
  // Excluded charges are left out of the total entirely — they are not split,
  // so we must never imply a member count. The old code fell through to the
  // "split N" branch below and said "split 4" for excluded charges, which
  // contradicted what you saw after clicking in.
  if (txn.status === 'excluded') return 'excluded'
  const pj = txn.participants_json
  if (!pj || pj.type === 'ask') return 'who splits this?'
  if (pj.type === 'all') return members.length ? `split ${members.length}` : 'split everyone'
  if (pj.type === 'single') return 'personal'
  // Custom / explicit member list
  const ids = pj.member_ids || []
  if (ids.length === 0) return 'who splits this?'
  const names = ids
    .map(id => members.find(m => m.id === id)?.name)
    .filter(Boolean)
  if (names.length <= 2) return names.join(' · ')
  return `split ${names.length}`
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ExpensesScreen() {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  // useQueryClient lets us invalidate (refresh) the transactions list after an import.
  const qc = useQueryClient()

  // Same PeopleSheet open/close logic as TripOverviewPage — auto-opens on new trip.
  const [peopleSheetOpen, setPeopleSheetOpen] = useState(
    () => location.state?.newTrip === true
  )

  // AddSheet open/close + transient "Added N expenses" banner state.
  // openImport in navigation state means another screen (e.g. the single-
  // expense form's "upload a statement instead" link) sent the user here
  // specifically to import — open the chooser immediately.
  const [addOpen, setAddOpen] = useState(location.state?.openImport === true)
  // Consume the flag so back/forward or refresh doesn't pop the sheet again.
  useEffect(() => {
    if (location.state?.openImport) {
      navigate(location.pathname, { replace: true, state: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [addedMsg, setAddedMsg] = useState(null)

  // ── Data fetching — same query keys as TripOverviewPage so the cache is shared ──
  const { data: group, isLoading: loadingGroup, isError: groupError } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => api.getGroup(groupId),
  })

  const { data: transactions = [] } = useQuery({
    queryKey: ['group-transactions', groupId],
    queryFn: () => api.getGroupTransactions(groupId),
    enabled: !!groupId,
  })

  if (loadingGroup) {
    return <div className="text-ink-500 animate-pulse-soft text-sm px-4 pt-8">Loading…</div>
  }

  // Group failed to load (deleted, stale link, or another account's group) —
  // say so instead of rendering a fake-empty home screen.
  if (groupError) {
    return <GroupNotFound />
  }

  const members = group?.members || []
  const currency = group?.base_currency || 'USD'
  const isOwner = !group?.owner_id || group.owner_id === user?.id

  // ── Derive stats from transactions ───────────────────────────────────────────
  // "active" = everything that isn't explicitly excluded from settlement
  const active = transactions.filter(t => t.status !== 'excluded')
  const totalSpent = active.reduce((s, t) => s + t.amount, 0)
  const hasTransactions = transactions.length > 0

  // Transactions flagged by needsReview (ambiguous split or undecoded name)
  const reviewList = active.filter(needsReview)

  // ── Group transactions by posted_date, newest day first ──────────────────────
  // We build a list of { date, txns } pairs sorted descending.
  const byDay = []
  const dayMap = new Map()
  for (const txn of transactions) {
    const day = txn.posted_date || 'Unknown'
    if (!dayMap.has(day)) {
      dayMap.set(day, [])
      byDay.push(day)
    }
    dayMap.get(day).push(txn)
  }
  // Sort newest date first (ISO strings sort lexicographically, so reverse works)
  byDay.sort((a, b) => b.localeCompare(a))

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto pb-24">

      {/* ── Success banner — shown for 6 s after a bulk import completes ──────── */}
      {addedMsg && (
        <div className="mb-4 rounded-xl bg-lime-400/10 border border-lime-400/25 px-4 py-3 text-sm text-lime-300">
          {addedMsg}
        </div>
      )}

      {/* ── 1. HEADER: trip name + people row ───────────────────────────────── */}
      <div className="px-4 pt-6 pb-4">
        <h1 className="font-display text-3xl font-bold text-ink-50 mb-3">
          {group?.name}
        </h1>

        {/* People row — single tap opens PeopleSheet, same pattern as TripOverviewPage */}
        <button
          onClick={() => setPeopleSheetOpen(true)}
          className="flex items-center gap-2 px-2 -mx-2 py-1 rounded-lg
                     hover:bg-ink-800/60 active:bg-ink-800 transition-colors
                     text-left w-full md:w-auto"
          title="Manage people"
        >
          <Users size={13} className="text-ink-500" />
          {members.length === 0 ? (
            <span className="text-xs text-lime-400 font-medium flex items-center gap-1">
              {/* Kind-aware copy — a household must never say "trip" */}
              <Plus size={11} /> {group?.kind === 'household' ? 'Add who lives here' : "Add who's on this trip"}
              <ChevronRight size={11} />
            </span>
          ) : (
            <>
              {/* Avatar stack — up to 5 initials bubbles then overflow count */}
              <div className="flex -space-x-1.5">
                {members.slice(0, 5).map((m, i) => {
                  if (m.is_placeholder) {
                    return (
                      <div
                        key={m.id}
                        title="Pending invite"
                        className="w-6 h-6 rounded-full bg-ink-800 border-2 border-ink-950 border-dashed flex items-center justify-center"
                      >
                        <Hourglass size={10} className="text-ink-500" />
                      </div>
                    )
                  }
                  const initials = (m.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                  return (
                    <div
                      key={m.id}
                      title={m.name}
                      className={clsx(
                        'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-ink-950',
                        MEMBER_COLORS[i % MEMBER_COLORS.length]
                      )}
                    >
                      {initials}
                    </div>
                  )
                })}
                {members.length > 5 && (
                  <div className="w-6 h-6 rounded-full bg-ink-700 border-2 border-ink-950 flex items-center justify-center text-[9px] font-bold text-ink-300">
                    +{members.length - 5}
                  </div>
                )}
              </div>
              <span className="text-xs text-ink-500 truncate max-w-[180px]">
                {members.filter(m => !m.is_placeholder).map(m => m.name).join(', ')}
                {members.some(m => m.is_placeholder) && (
                  <span className="text-ink-500">
                    {' '}· {members.filter(m => m.is_placeholder).length} pending
                  </span>
                )}
              </span>
              <span className="ml-1 flex items-center gap-0.5 text-[11px] font-medium text-lime-400 hover:text-lime-300">
                <Plus size={10} />
                Add people
              </span>
            </>
          )}
        </button>
      </div>

      {/* ── 2. MONEY SUMMARY ─────────────────────────────────────────────────── */}
      {hasTransactions && (
        <div className="px-4 mb-2 animate-slide-up">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatCard
              label="Total spent"
              value={formatAmount(totalSpent, currency)}
              color="text-ink-100"
            />
            {/* Tappable: the stat doubles as the door to the full transactions
                manager (filters, bulk edit, exclude) — see the UX note on the
                "all reviewed" row below for why a permanent door matters. */}
            <StatCard
              label="Expenses"
              value={active.length}
              color="text-ink-100"
              ariaLabel="View all expenses"
              onClick={() => navigate(`/groups/${groupId}/add-interim`)}
            />
          </div>
          {/* MyBalanceWidget shows the personal balance if user is linked to a member */}
          <MyBalanceWidget groupId={groupId} />

          {/* Recurring bills — the household killer feature (rent, utilities,
              Netflix). Households only: trips rarely have recurring charges.
              (Restored here after TripOverviewPage — its old home — was
              replaced by this screen and quietly took the section with it.) */}
          {group?.kind === 'household' && (
            <div className="mt-4">
              <RecurringExpenses
                groupId={groupId}
                members={members}
                baseCurrency={group?.base_currency || 'USD'}
                transactions={transactions}
              />
            </div>
          )}
        </div>
      )}

      {/* ── 3. "NEEDS A QUICK LOOK" NUDGE ────────────────────────────────────── */}
      {/* Only shown when there are genuinely ambiguous transactions. Amber styling
          draws attention without being alarming — these need a decision, not alarm. */}
      {reviewList.length > 0 && (
        <div
          className="mx-4 mb-4 flex items-center gap-3 px-4 py-3 rounded-xl
                     bg-amber-400/8 border border-amber-400/20
                     cursor-pointer hover:bg-amber-400/12 transition-colors
                     animate-slide-up"
          onClick={() => navigate(`/groups/${groupId}/add-interim`)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && navigate(`/groups/${groupId}/add-interim`)}
        >
          <AlertTriangle size={15} className="text-amber-400 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-400">
            {/* Test expects text matching /need a quick look/i */}
            {reviewList.length} need a quick look — tap to review
          </div>
          <ChevronRight size={14} className="text-amber-400/60 flex-shrink-0" />
        </div>
      )}

      {/* When NOTHING needs review, a quiet row takes the banner's place.
          UX bug (user-reported): the amber banner was the only door to the
          full transactions manager, so finishing your reviews deleted the
          navigation. The door must exist in both states — amber when there's
          work, calm when there isn't. */}
      {hasTransactions && reviewList.length === 0 && (
        <div
          className="mx-4 mb-4 flex items-center gap-3 px-4 py-3 rounded-xl
                     bg-ink-800/40 border border-ink-700/60
                     cursor-pointer hover:bg-ink-800 hover:border-lime-400/30 transition-colors
                     animate-slide-up"
          onClick={() => navigate(`/groups/${groupId}/add-interim`)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && navigate(`/groups/${groupId}/add-interim`)}
        >
          <Receipt size={15} className="text-ink-400 flex-shrink-0" />
          <div className="flex-1 text-sm text-ink-300">
            All reviewed — browse or edit the full list
          </div>
          <ChevronRight size={14} className="text-ink-500 flex-shrink-0" />
        </div>
      )}

      {/* ── 4. TRANSACTION LIST grouped by day ──────────────────────────────── */}
      {/* Empty state — tapping this card (or the floating button) opens AddSheet */}
      {!hasTransactions && (
        <div
          className="mx-4 mt-4 px-5 py-8 rounded-2xl border border-dashed border-ink-700 text-center animate-slide-up
                     cursor-pointer hover:border-lime-400/40 hover:bg-ink-800/20 transition-colors"
          role="button"
          tabIndex={0}
          onClick={() => setAddOpen(true)}
          onKeyDown={e => e.key === 'Enter' && setAddOpen(true)}
          aria-label="Add your first expense"
        >
          <Receipt size={28} className="text-ink-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-ink-400 mb-1">No expenses yet</p>
          <p className="text-xs text-ink-500">
            Tap <span className="text-lime-400 font-semibold">+ Add</span> to log your first one.
          </p>
        </div>
      )}

      {byDay.map(day => (
        <div key={day} className="mb-1">
          {/* Day label */}
          <div className="px-4 py-1.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-500">
              {day === 'Unknown' ? 'Unknown date' : formatDateLabel(day)}
            </span>
          </div>

          {/* Transaction rows for this day */}
          <div className="space-y-1 px-4">
            {dayMap.get(day).map(txn => {
              const name = displayName(txn)
              const needsLook = needsReview(txn)
              const CategoryIcon = categoryIcon(txn.category)
              // txn.amount is ALWAYS in the group's base currency — the backend
              // converts foreign charges at import. txn.currency describes the
              // ORIGINAL charge (e.g. JPY), so formatting amount with it would
              // show a dollar value behind a yen sign. Always use the base.
              const amt = formatAmount(txn.amount, currency)
              const split = splitSummary(txn, members)

              return (
                <button
                  key={txn.id}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl',
                    'border transition-all text-left',
                    needsLook
                      ? 'bg-amber-400/5 border-amber-400/20 hover:bg-amber-400/10 hover:border-amber-400/35'
                      : 'bg-ink-900 border-ink-800 hover:bg-ink-800/60 hover:border-ink-700',
                  )}
                  onClick={() => {
                    // TODO(2b): inline split editor
                    navigate(`/groups/${groupId}/add-interim`)
                  }}
                >
                  {/* Category icon bubble */}
                  <div className={clsx(
                    'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center',
                    needsLook ? 'bg-amber-400/10' : 'bg-lime-400/10',
                  )}>
                    <CategoryIcon
                      size={14}
                      className={needsLook ? 'text-amber-400' : 'text-lime-400'}
                    />
                  </div>

                  {/* Name + optional raw description as memory aid */}
                  <div className="flex-1 min-w-0">
                    {/* Primary display name — AI-cleaned or raw fallback */}
                    <div className="text-sm font-semibold text-ink-100 truncate">
                      {name}
                    </div>
                    {/* Show the faded raw bank string when a cleaned name exists.
                        This is the "memory aid" so users recognize what it was. */}
                    {txn.display_name && (
                      <div className="text-[10px] text-ink-500 truncate mt-0.5">
                        {txn.description_raw}
                      </div>
                    )}
                    {/* Who splits this? */}
                    <div className="text-[10px] text-ink-500 mt-0.5">{split}</div>
                  </div>

                  {/* Right side: amount or "review" badge */}
                  {needsLook ? (
                    <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/30">
                      review
                    </span>
                  ) : (
                    <div className="flex-shrink-0 text-right">
                      <div className="text-sm font-bold text-lime-400 font-mono">{amt}</div>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      {/* ── 5. "+ Add" FLOATING BUTTON ───────────────────────────────────────── */}
      {/* Lime-colored, prominent, fixed to bottom-right.
          Opens AddSheet so the user can pick import or manual entry. */}
      <button
        onClick={() => setAddOpen(true)}
        className="fixed bottom-20 right-5 z-20
                   w-14 h-14 rounded-full bg-lime-400 text-ink-950
                   flex items-center justify-center shadow-lg shadow-lime-400/30
                   hover:bg-lime-300 active:scale-95 transition-all"
        aria-label="Add expense"
      >
        <Plus size={26} strokeWidth={2.5} />
      </button>

      {/* ── 6. STICKY "SETTLE UP" BUTTON ─────────────────────────────────────── */}
      {/* This is the "finish line" — always visible so the user always knows
          they can resolve who owes what. Pinned to the bottom of the screen.
          Role=button with accessible name for tests: getByRole('button', { name: /settle up/i }) */}
      <div className="fixed bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-2 bg-gradient-to-t from-ink-950 via-ink-950/95 to-transparent">
        <button
          onClick={() => navigate(`/groups/${groupId}/settle`)}
          className="w-full max-w-2xl mx-auto flex items-center justify-center gap-2
                     px-6 py-3.5 rounded-2xl bg-ink-800 border border-ink-600
                     text-sm font-semibold text-ink-100
                     hover:bg-ink-700 hover:border-ink-500 transition-all"
        >
          {/* Type-aware finish line: a trip settles once at the end; a
              household never "finishes" — it settles the running month. */}
          {group?.kind === 'household' ? 'Settle this month' : 'Settle up'}
          <ChevronRight size={16} className="text-lime-400" />
        </button>
      </div>

      {/* ── PeopleSheet — lazy-rendered so it doesn't load in tests unless opened ── */}
      {peopleSheetOpen && (
        <PeopleSheet
          group={group}
          members={members}
          isOwner={isOwner}
          onClose={() => setPeopleSheetOpen(false)}
          onEditHandles={() => {}} // simplified — full handles editor is TripOverviewPage's concern
        />
      )}

      {/* ── AddSheet — opens when the floating "+ Add" button or empty-state is tapped ── */}
      {addOpen && (
        <AddSheet
          group={group}
          members={members}
          onClose={() => setAddOpen(false)}
          onAddedBulk={(r) => {
            // Close the sheet first.
            setAddOpen(false)
            // Refresh the transactions list so the newly-imported expenses appear immediately.
            qc.invalidateQueries(['group-transactions', groupId])
            // Build a friendly "Added N expenses" banner message.
            const n = r?.transaction_count ?? 0
            const m = r?.needs_review_count ?? 0
            setAddedMsg(`Added ${n} ${n === 1 ? 'expense' : 'expenses'}${m ? ` — ${m} need a quick look` : ''}.`)
            // Auto-dismiss the banner after 6 seconds.
            setTimeout(() => setAddedMsg(null), 6000)
          }}
        />
      )}
    </div>
  )
}

